import { Injectable, signal } from '@angular/core';

import type {
  ConfirmOptions,
  DialogState,
  PromptOptions,
} from './confirm-dialog.types';

let nextId = 1;

/**
 * ConfirmDialogService — global, signal-based. Substitui `window.confirm`
 * e `window.prompt` por modal customizado animado por contexto. Espelha
 * o padrão do SnackbarService.
 *
 * Apenas 1 modal por vez — empilhamento é confuso em fluxo gamificado.
 * Se chamar `confirm()` enquanto já tem modal aberto, o anterior é
 * dismissed (resolve=false/null) antes de abrir o novo.
 *
 * Uso:
 *   const ok = await this.confirm.confirm({ variant: 'sink', ... });
 *   const motivo = await this.confirm.prompt({ variant: 'destructive-prompt', ... });
 */
@Injectable({ providedIn: 'root' })
export class ConfirmDialogService {
  /** Estado atual — null quando não há dialog aberto. */
  readonly dialog = signal<DialogState | null>(null);

  confirm(opts: ConfirmOptions): Promise<boolean> {
    return new Promise<boolean>((resolveOuter) => {
      this.dismissCurrent();
      const state: DialogState = {
        id: nextId++,
        kind: 'confirm',
        options: opts,
        resolve: (value) => {
          this.dialog.set(null);
          resolveOuter(value === true);
        },
      };
      this.dialog.set(state);
    });
  }

  prompt(opts: PromptOptions): Promise<string | null> {
    return new Promise<string | null>((resolveOuter) => {
      this.dismissCurrent();
      const state: DialogState = {
        id: nextId++,
        kind: 'prompt',
        options: opts,
        resolve: (value) => {
          this.dialog.set(null);
          if (typeof value === 'string') {
            resolveOuter(value);
            return;
          }
          resolveOuter(null);
        },
      };
      this.dialog.set(state);
    });
  }

  /**
   * Chamado pelo host quando user clica botão / backdrop / ESC.
   * Resolve a Promise com o valor passado.
   */
  resolve(value: boolean | string | null): void {
    const cur = this.dialog();
    if (!cur) return;
    cur.resolve(value);
  }

  private dismissCurrent(): void {
    const cur = this.dialog();
    if (cur) {
      cur.resolve(cur.kind === 'prompt' ? null : false);
    }
  }
}
